/**
 * Structured Base Policies Form Component.
 *
 * Replaces raw JSON textarea with structured form fields for security policies.
 * Provides a toggle to switch between structured view and raw JSON editor.
 *
 * Sprint: 44, Task: S44-D03
 */

import { useState, useCallback, useEffect } from 'react';
import { useI18n } from '@/i18n';
import { Input, Label, Textarea } from '@/components/ui';
import { Code, FormInput } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface BasePolicies {
  max_calls_per_turn?: number;
  max_timeout_seconds?: number;
  max_response_size_bytes?: number;
  rate_limit_per_minute?: number;
  rate_limit_per_hour?: number;
  require_https?: boolean;
  allowed_http_methods?: string[];
  blocked_http_methods?: string[];
  blocked_tool_names?: string[];
  blocked_endpoints?: string[];
  [key: string]: unknown;
}

interface BasePoliciesFormProps {
  /** JSON string value from react-hook-form */
  value: string;
  /** Called with updated JSON string */
  onChange: (value: string) => void;
  /** Validation error message */
  error?: string;
}

// =============================================================================
// Constants
// =============================================================================

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

// =============================================================================
// Helpers
// =============================================================================

function parseJsonSafe(str: string): BasePolicies {
  try {
    const parsed = JSON.parse(str || '{}');
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function formatJson(obj: BasePolicies): string {
  return JSON.stringify(obj, null, 2);
}

// =============================================================================
// Tag Input Sub-component
// =============================================================================

function TagInput({
  label,
  help,
  tags,
  onTagsChange,
  placeholder,
}: {
  label: string;
  help?: string;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  placeholder?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onTagsChange([...tags, trimmed]);
    }
    setInputValue('');
  };

  const removeTag = (index: number) => {
    onTagsChange(tags.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((tag, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-xs"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(i)}
              className="text-muted-foreground hover:text-destructive ml-0.5"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder={placeholder || 'Type and press Enter'}
          className="text-sm"
        />
      </div>
    </div>
  );
}

// =============================================================================
// Multi-select Checkboxes Sub-component
// =============================================================================

function MethodCheckboxes({
  label,
  help,
  selected,
  onChange,
}: {
  label: string;
  help?: string;
  selected: string[];
  onChange: (methods: string[]) => void;
}) {
  const toggle = (method: string) => {
    if (selected.includes(method)) {
      onChange(selected.filter((m) => m !== method));
    } else {
      onChange([...selected, method]);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
      <div className="flex flex-wrap gap-2">
        {HTTP_METHODS.map((method) => (
          <label key={method} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={selected.includes(method)}
              onChange={() => toggle(method)}
              className="rounded border-gray-300"
            />
            {method}
          </label>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function BasePoliciesForm({ value, onChange, error }: BasePoliciesFormProps) {
  const { t } = useI18n();
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [jsonValue, setJsonValue] = useState(value);
  const [policies, setPolicies] = useState<BasePolicies>(() => parseJsonSafe(value));

  // Sync from parent when value changes externally
  useEffect(() => {
    const parsed = parseJsonSafe(value);
    setPolicies(parsed);
    setJsonValue(value);
  }, [value]);

  const updateField = useCallback(
    (field: keyof BasePolicies, val: unknown) => {
      const updated = { ...policies, [field]: val };
      // Remove undefined/empty fields
      if (val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
        delete updated[field];
      }
      setPolicies(updated);
      const json = formatJson(updated);
      setJsonValue(json);
      onChange(json);
    },
    [policies, onChange]
  );

  const handleJsonChange = useCallback(
    (raw: string) => {
      setJsonValue(raw);
      onChange(raw);
      // Try to parse for structured view
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null) {
          setPolicies(parsed);
        }
      } catch {
        // Invalid JSON — keep raw, structured view will use last valid state
      }
    },
    [onChange]
  );

  const toggleMode = () => {
    if (isJsonMode) {
      // Switching from JSON → structured: re-parse the raw value
      const parsed = parseJsonSafe(jsonValue);
      setPolicies(parsed);
    } else {
      // Switching from structured → JSON: format current policies
      setJsonValue(formatJson(policies));
    }
    setIsJsonMode(!isJsonMode);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>{t('admin.systemTemplates.form.basePolicies')}</Label>
          <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.basePoliciesHelp')}</p>
        </div>
        <button
          type="button"
          onClick={toggleMode}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded border"
        >
          {isJsonMode ? (
            <>
              <FormInput className="w-3.5 h-3.5" />
              {t('admin.systemTemplates.form.structuredView')}
            </>
          ) : (
            <>
              <Code className="w-3.5 h-3.5" />
              {t('admin.systemTemplates.form.jsonView')}
            </>
          )}
        </button>
      </div>

      {isJsonMode ? (
        <div className="space-y-1.5">
          <Textarea
            value={jsonValue}
            onChange={(e) => handleJsonChange(e.target.value)}
            rows={12}
            className="font-mono text-xs"
            placeholder="{}"
          />
          {error && (
            <p className="text-xs text-destructive">{t('admin.systemTemplates.form.invalidJson')}</p>
          )}
        </div>
      ) : (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          {/* Rate Limiting */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('admin.systemTemplates.form.policies.maxCallsPerTurn')}</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={policies.max_calls_per_turn ?? ''}
                onChange={(e) =>
                  updateField('max_calls_per_turn', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="5"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('admin.systemTemplates.form.policies.maxTimeoutSeconds')}</Label>
              <Input
                type="number"
                min={1}
                max={600}
                value={policies.max_timeout_seconds ?? ''}
                onChange={(e) =>
                  updateField('max_timeout_seconds', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="30"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('admin.systemTemplates.form.policies.rateLimitPerMinute')}</Label>
              <Input
                type="number"
                min={1}
                max={10000}
                value={policies.rate_limit_per_minute ?? ''}
                onChange={(e) =>
                  updateField('rate_limit_per_minute', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="30"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('admin.systemTemplates.form.policies.rateLimitPerHour')}</Label>
              <Input
                type="number"
                min={1}
                max={100000}
                value={policies.rate_limit_per_hour ?? ''}
                onChange={(e) =>
                  updateField('rate_limit_per_hour', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="300"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('admin.systemTemplates.form.policies.maxResponseSizeBytes')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1024}
                  max={104857600}
                  value={policies.max_response_size_bytes ?? ''}
                  onChange={(e) =>
                    updateField('max_response_size_bytes', e.target.value ? Number(e.target.value) : undefined)
                  }
                  placeholder="5242880"
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {policies.max_response_size_bytes
                    ? policies.max_response_size_bytes >= 1048576
                      ? `${(policies.max_response_size_bytes / 1048576).toFixed(1)} MB`
                      : `${(policies.max_response_size_bytes / 1024).toFixed(0)} KB`
                    : ''}
                </span>
              </div>
            </div>
          </div>

          {/* HTTPS Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="require_https"
              checked={policies.require_https ?? true}
              onChange={(e) => updateField('require_https', e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="require_https">
              {t('admin.systemTemplates.form.policies.requireHttps')}
            </Label>
          </div>

          {/* HTTP Methods */}
          <MethodCheckboxes
            label={t('admin.systemTemplates.form.policies.allowedHttpMethods')}
            help={t('admin.systemTemplates.form.policies.allowedHttpMethodsHelp')}
            selected={policies.allowed_http_methods ?? []}
            onChange={(methods) => updateField('allowed_http_methods', methods)}
          />

          <MethodCheckboxes
            label={t('admin.systemTemplates.form.policies.blockedHttpMethods')}
            help={t('admin.systemTemplates.form.policies.blockedHttpMethodsHelp')}
            selected={policies.blocked_http_methods ?? []}
            onChange={(methods) => updateField('blocked_http_methods', methods)}
          />

          {/* Blocked items */}
          <TagInput
            label={t('admin.systemTemplates.form.policies.blockedToolNames')}
            help={t('admin.systemTemplates.form.policies.blockedToolNamesHelp')}
            tags={policies.blocked_tool_names ?? []}
            onTagsChange={(tags) => updateField('blocked_tool_names', tags)}
            placeholder="e.g. delete_user"
          />

          <TagInput
            label={t('admin.systemTemplates.form.policies.blockedEndpoints')}
            help={t('admin.systemTemplates.form.policies.blockedEndpointsHelp')}
            tags={policies.blocked_endpoints ?? []}
            onTagsChange={(tags) => updateField('blocked_endpoints', tags)}
            placeholder="e.g. /admin/*"
          />

          {error && (
            <p className="text-xs text-destructive">{t('admin.systemTemplates.form.invalidJson')}</p>
          )}
        </div>
      )}
    </div>
  );
}
